import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../ui/LoadingSpinner';
import NumberInput from '../ui/NumberInput';
import EmptyState from '../ui/EmptyState';

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

interface Payslip {
  id: string;
  payslipId: string;
  userGymMappingId: string;
  teamMember: {
    id: string;
    employeeId?: string | null;
    name: string;
    displayName?: string;
    email: string;
    phoneNo?: string;
    role: string;
  };
  gym?: {
    id: string;
    name: string;
    displayName: string;
    gstin?: string | null;
    includeGstinInInvoices?: boolean;
    registeredAddress?: string | null;
    address?: string | null;
    phoneNumbers?: string[] | null;
    email?: string | null;
  };
  payPeriodStart: string;
  payPeriodEnd: string;
  payDate: string;
  totalDays: number;
  presentDays: number;
  absentDays: number;
  leaveDays: number;
  overtimeHours: number;
  undertimeHours: number;
  grossSalary: number;
  totalDeductions: number;
  totalAdditions: number;
  netSalary: number;
  status: 'draft' | 'finalized' | 'paid' | 'deleted';
  isProrated?: boolean;
  notes?: string;
  pdfUrl?: string;
  components: Array<{
    id: string;
    componentType: string;
    componentName: string;
    amount: number;
    isDeduction: boolean;
    description?: string;
  }>;
}

interface PayslipGenerationData {
  teamMember: {
    id: string;
    name: string;
    displayName?: string;
    email: string;
    phoneNo?: string;
  };
  salary: {
    baseSalary: number;
    currency: string;
    workingHoursPerDay: number;
    workingDaysPerMonth: number;
    overtimeRate?: number;
    undertimeDeductionRate?: number;
  };
  attendance: {
    totalDays: number;
    presentDays: number;
    absentDays: number;
    leaveDays: number;
    overtimeHours: number;
    undertimeHours: number;
    futureDays?: number;
  };
  leaves?: Array<{
    id: string;
    leaveType: string;
    startDate: string;
    endDate: string;
    isHalfDay: boolean;
    halfDayType?: string | null;
  }>;
  trainerEngagement?: {
    trainerId: string;
    trainerName: string;
    memberCount: number;
    eligibleMemberCount: number;
    totalEngagementDays: number;
    totalCheckIns: number;
    threshold: number;
    members: Array<{
      memberId: string;
      memberName: string;
      phoneNumber: string;
      engagementDays: number;
      engagementPercent: number;
      checkIns: number;
      currentlyAssigned: boolean;
      eligible: boolean;
    }>;
  } | null;
}

interface TeamPayslipsTabProps {
  teamMembers: Array<{ id: string; userId: string; name: string; displayName?: string }>;
}

export default function TeamPayslipsTab({ teamMembers }: TeamPayslipsTabProps) {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [generationData, setGenerationData] = useState<PayslipGenerationData | null>(null);
  const [loadingGenerationData, setLoadingGenerationData] = useState(false);
  // Set while editing an existing draft payslip (reuses the generate modal, pre-filled). Null = creating a new one.
  const [editingPayslipId, setEditingPayslipId] = useState<string | null>(null);
  const [componentsError, setComponentsError] = useState<string | null>(null);

  // Adjustable eligibility threshold for the Trainer Member Engagement section.
  // Defaults to the gym setting; eligibility is recomputed client-side from engagementPercent.
  const gymEngagementThreshold = generationData?.trainerEngagement?.threshold ?? 50;
  const [engagementThreshold, setEngagementThreshold] = useState<number>(50);
  useEffect(() => {
    // Re-sync to the gym default whenever details are loaded for a (different) trainer.
    if (generationData?.trainerEngagement) {
      setEngagementThreshold(generationData.trainerEngagement.threshold);
    }
  }, [generationData?.trainerEngagement?.trainerId, generationData?.trainerEngagement?.threshold]);

  const engagementView = useMemo(() => {
    const te = generationData?.trainerEngagement;
    if (!te) return null;
    const members = te.members.map(m => ({ ...m, eligible: m.engagementPercent >= engagementThreshold }));
    return { members, eligibleMemberCount: members.filter(m => m.eligible).length };
  }, [generationData?.trainerEngagement, engagementThreshold]);
  // Helper function to convert number to words (simplified for Indian format)
  const convertToWords = (num: number): string => {
    if (num === 0) return 'Zero Rupees';

    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertHundreds = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return units[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + units[n % 10] : '');
      return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertHundreds(n % 100) : '');
    };

    const intPart = Math.floor(num);
    if (intPart < 1000) return convertHundreds(intPart) + ' Rupees';
    if (intPart < 100000) {
      const thousands = Math.floor(intPart / 1000);
      const remainder = intPart % 1000;
      return convertHundreds(thousands) + ' Thousand' + (remainder !== 0 ? ' ' + convertHundreds(remainder) : '') + ' Rupees';
    }
    if (intPart < 10000000) {
      const lakhs = Math.floor(intPart / 100000);
      let remainder = intPart % 100000;
      let result = convertHundreds(lakhs) + ' Lakh';
      if (remainder >= 1000) {
        const thousands = Math.floor(remainder / 1000);
        result += ' ' + convertHundreds(thousands) + ' Thousand';
        remainder = remainder % 1000;
      }
      if (remainder !== 0) result += ' ' + convertHundreds(remainder);
      return result + ' Rupees';
    }
    return intPart.toLocaleString('en-IN') + ' Rupees';
  };

  const [filters, setFilters] = useState({
    teamMemberId: '',
    lastMonths: 3, // Default to last 3 months
    showFuture: false // Default to not show future payslips
  });

  const emptyFormData = {
    userGymMappingId: '',
    payPeriodStart: '',
    payPeriodEnd: '',
    payDate: '',
    overtimeHours: 0,
    undertimeHours: 0,
    prorate: false,
    components: [] as Array<{
      componentType: string;
      componentName: string;
      amount: number;
      isDeduction: boolean;
      description?: string;
    }>,
    notes: ''
  };

  const [formData, setFormData] = useState(emptyFormData);

  // Live preview of the payslip being built in the modal - mirrors the backend's
  // computePayslipFinancials so the numbers on the right update as fields change,
  // without round-tripping to the API on every keystroke.
  const payslipPreview = useMemo(() => {
    if (!generationData) return null;

    const baseSalary = generationData.salary.baseSalary;
    let grossSalary = baseSalary;
    let prorateInfo: { periodDays: number; monthDays: number } | null = null;

    if (formData.prorate && baseSalary > 0 && formData.payPeriodStart && formData.payPeriodEnd) {
      const start = new Date(formData.payPeriodStart);
      const end = new Date(formData.payPeriodEnd);
      if (end >= start) {
        const periodDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
        const monthDays = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
        grossSalary = Math.round((baseSalary * periodDays / monthDays) * 100) / 100;
        prorateInfo = { periodDays, monthDays };
      }
    }

    const overtimeRate = generationData.salary.overtimeRate || 0;
    const undertimeDeductionRate = generationData.salary.undertimeDeductionRate || 0;

    const lines: Array<{ name: string; amount: number; isDeduction: boolean; description?: string }> = [];
    let totalAdditions = 0;
    let totalDeductions = 0;

    if (grossSalary > 0) {
      lines.push({
        name: 'Basic Salary',
        amount: grossSalary,
        isDeduction: false,
        description: prorateInfo ? `Prorated: ${prorateInfo.periodDays} of ${prorateInfo.monthDays} days in month` : undefined
      });
    }

    if (formData.overtimeHours > 0 && overtimeRate > 0) {
      const amount = Math.round(formData.overtimeHours * overtimeRate * 100) / 100;
      totalAdditions += amount;
      lines.push({ name: 'Overtime', amount, isDeduction: false, description: `${formData.overtimeHours} hours @ ${overtimeRate}/hour` });
    }

    if (formData.undertimeHours > 0 && undertimeDeductionRate > 0) {
      const amount = Math.round(formData.undertimeHours * undertimeDeductionRate * 100) / 100;
      totalDeductions += amount;
      lines.push({ name: 'Undertime Deduction', amount, isDeduction: true, description: `${formData.undertimeHours} hours @ ${undertimeDeductionRate}/hour` });
    }

    for (const c of formData.components) {
      const amount = Number(c.amount) || 0;
      if (c.isDeduction) totalDeductions += amount; else totalAdditions += amount;
      lines.push({ name: c.componentName?.trim() || '(unnamed component)', amount, isDeduction: c.isDeduction, description: c.description || undefined });
    }

    const netSalary = Math.round((grossSalary + totalAdditions - totalDeductions) * 100) / 100;

    return { grossSalary, totalAdditions, totalDeductions, netSalary, lines, prorateInfo };
  }, [generationData, formData.prorate, formData.payPeriodStart, formData.payPeriodEnd, formData.overtimeHours, formData.undertimeHours, formData.components]);

  const getEndOfNextMonth = () => {
    const now = new Date();
    const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    return endOfNextMonth.toISOString().split('T')[0];
  };

  const validatePayslipDates = () => {
    if (!formData.payPeriodStart || !formData.payPeriodEnd || !formData.payDate) {
      return true;
    }

    const payPeriodStart = new Date(formData.payPeriodStart);
    const payPeriodEnd = new Date(formData.payPeriodEnd);
    const payDate = new Date(formData.payDate);
    const endOfNextMonth = new Date(getEndOfNextMonth());

    if (payPeriodEnd <= payPeriodStart) {
      toast.error('Pay period end must be after start date');
      return false;
    }

    if (payPeriodEnd > endOfNextMonth) {
      toast.error(`Pay period end cannot be later than ${getEndOfNextMonth()}`);
      return false;
    }

    if (payDate > endOfNextMonth) {
      toast.error(`Pay date cannot be later than ${getEndOfNextMonth()}`);
      return false;
    }

    return true;
  };

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const queryParams = new URLSearchParams();
      if (filters.teamMemberId) queryParams.append('teamMemberId', filters.teamMemberId);

      // Calculate date range based on lastMonths filter
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      // Start from N months ago (beginning of that month)
      const startDate = new Date(currentYear, currentMonth - filters.lastMonths + 1, 1);

      // End date is either end of current month or end of next month if showFuture is true
      let endDate;
      if (filters.showFuture) {
        // End of next month
        endDate = new Date(currentYear, currentMonth + 2, 0);
      } else {
        // End of current month
        endDate = new Date(currentYear, currentMonth + 1, 0);
      }

      queryParams.append('startDate', startDate.toISOString().split('T')[0]);
      queryParams.append('endDate', endDate.toISOString().split('T')[0]);

      const response = await fetch(`${API_BASE_URL}/api/payslips?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPayslips(data.data || []);
      } else {
        throw new Error('Failed to fetch payslips');
      }
    } catch (error) {
      console.error('Error fetching payslips:', error);
      toast.error('Failed to load payslips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayslips();
  }, [filters]);

  const handleDeletePayslip = async () => {
    if (!selectedPayslip) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No access token');

      const response = await fetch(`${API_BASE_URL}/api/payslips/${selectedPayslip.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete payslip');
      }

      toast.success('Payslip deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedPayslip(null);
      fetchPayslips();
    } catch (error: any) {
      console.error('Error deleting payslip:', error);
      toast.error(error.message || 'Failed to delete payslip');
    }
  };

  const fetchGenerationData = async () => {
    if (!formData.userGymMappingId || !formData.payPeriodStart || !formData.payPeriodEnd) {
      toast.error('Please select team member and pay period');
      return;
    }
    if (!validatePayslipDates()) {
      return;
    }

    try {
      setLoadingGenerationData(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const queryParams = new URLSearchParams({
        userGymMappingId: formData.userGymMappingId,
        payPeriodStart: formData.payPeriodStart,
        payPeriodEnd: formData.payPeriodEnd
      });

      const response = await fetch(`${API_BASE_URL}/api/payslips/generate-data?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGenerationData(data.data);
        // Pre-fill overtime/undertime from fetched data, unless we're editing an
        // existing draft (in which case its saved hours should stick).
        if (!editingPayslipId) {
          setFormData(prev => ({
            ...prev,
            overtimeHours: data.data.attendance.overtimeHours,
            undertimeHours: data.data.attendance.undertimeHours
          }));
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch generation data');
      }
    } catch (error: any) {
      console.error('Error fetching generation data:', error);
      toast.error(error.message || 'Failed to load generation data');
    } finally {
      setLoadingGenerationData(false);
    }
  };

  // Auto-load attendance/salary details once when opening the modal to edit a draft.
  useEffect(() => {
    if (editingPayslipId && showGenerateModal && !generationData && !loadingGenerationData) {
      fetchGenerationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPayslipId, showGenerateModal]);

  const resetGenerateModal = () => {
    setFormData(emptyFormData);
    setGenerationData(null);
    setEditingPayslipId(null);
    setComponentsError(null);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    resetGenerateModal();
  };

  // Reopens the generate modal, pre-filled with an existing draft's details, so
  // it can be adjusted and re-submitted (PATCHed) instead of creating a new one.
  const handleEditPayslip = (payslip: Payslip) => {
    const toDateInput = (d: string) => (d ? d.split('T')[0] : '');
    const autoTypes = ['salary', 'overtime', 'undertime'];
    setFormData({
      userGymMappingId: payslip.userGymMappingId,
      payPeriodStart: toDateInput(payslip.payPeriodStart),
      payPeriodEnd: toDateInput(payslip.payPeriodEnd),
      payDate: toDateInput(payslip.payDate),
      overtimeHours: Number(payslip.overtimeHours) || 0,
      undertimeHours: Number(payslip.undertimeHours) || 0,
      prorate: !!payslip.isProrated,
      components: payslip.components
        .filter(c => !autoTypes.includes(c.componentType))
        .map(c => ({
          componentType: c.componentType,
          componentName: c.componentName,
          amount: Number(c.amount),
          isDeduction: c.isDeduction,
          description: c.description || ''
        })),
      notes: payslip.notes || ''
    });
    setGenerationData(null);
    setComponentsError(null);
    setEditingPayslipId(payslip.id);
    setShowPayslipModal(false);
    setShowGenerateModal(true);
  };

  const handleSubmitPayslip = async () => {
    const missingNameIndex = formData.components.findIndex(c => !c.componentName || !c.componentName.trim());
    if (missingNameIndex !== -1) {
      setComponentsError('Please enter a name for every salary component before continuing.');
      return;
    }
    setComponentsError(null);

    try {
      if (!validatePayslipDates()) {
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const payload: Record<string, unknown> = {
        payPeriodStart: formData.payPeriodStart,
        payPeriodEnd: formData.payPeriodEnd,
        payDate: formData.payDate,
        overtimeHours: formData.overtimeHours,
        undertimeHours: formData.undertimeHours,
        prorate: formData.prorate,
        components: formData.components.map(c => ({ ...c, componentName: c.componentName.trim() })),
        notes: formData.notes
      };
      if (!editingPayslipId) {
        payload.userGymMappingId = formData.userGymMappingId;
      }

      const url = editingPayslipId
        ? `${API_BASE_URL}/api/payslips/${editingPayslipId}`
        : `${API_BASE_URL}/api/payslips`;

      const response = await fetch(url, {
        method: editingPayslipId ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editingPayslipId ? 'update' : 'generate'} payslip`);
      }

      toast.success(editingPayslipId ? 'Payslip updated successfully' : 'Payslip generated successfully');
      setShowGenerateModal(false);
      resetGenerateModal();
      fetchPayslips();
    } catch (error: any) {
      console.error('Error saving payslip:', error);
      toast.error(error.message || 'Failed to save payslip');
    }
  };

  const addComponent = () => {
    setFormData(prev => ({
      ...prev,
      components: [
        ...prev.components,
        {
          componentType: 'benefit',
          componentName: '',
          amount: 0,
          isDeduction: false,
          description: ''
        }
      ]
    }));
  };

  const removeComponent = (index: number) => {
    setComponentsError(null);
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  const updateComponent = (index: number, field: string, value: any) => {
    setComponentsError(null);
    setFormData(prev => ({
      ...prev,
      components: prev.components.map((comp, i) =>
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'finalized': return 'bg-green-100 text-green-800';
      case 'paid': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Team Member</label>
            <select
              value={filters.teamMemberId}
              onChange={(e) => setFilters(prev => ({ ...prev, teamMemberId: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Members</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.userId}>{member.displayName || member.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Time Period</label>
            <select
              value={filters.lastMonths}
              onChange={(e) => setFilters(prev => ({ ...prev, lastMonths: parseInt(e.target.value) }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value={1}>Last 1 month</option>
              <option value={2}>Last 2 months</option>
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
            </select>
          </div>
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showFuture}
                onChange={(e) => setFilters(prev => ({ ...prev, showFuture: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-gray-700">Show future payslips</span>
            </label>
          </div>
          <button
            onClick={() => setFilters({
              teamMemberId: '',
              lastMonths: 3,
              showFuture: false
            })}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Generate Payslip</span>
          </button>
        </div>
      </div>

      {/* Payslips List */}
      {payslips.length === 0 ? (
        <EmptyState
          title="No payslips"
          description="Generate payslips for your team members to view them here."
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payslips.map((payslip) => (
                  <tr key={payslip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{payslip.teamMember.displayName || payslip.teamMember.name}</div>
                        <div className="text-sm text-gray-500">{payslip.gym?.displayName || ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payslip.payPeriodStart)} - {formatDate(payslip.payPeriodEnd)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payslip.grossSalary)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(payslip.netSalary)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(payslip.status)}`}>
                        {payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1)}
                      </span>
                      {payslip.isProrated && (
                        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800" title="Salary was prorated for a partial pay period">
                          Prorated
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            setSelectedPayslip(payslip);
                            setShowPayslipModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </button>
                        {payslip.status === 'draft' && (
                          <button
                            onClick={() => handleEditPayslip(payslip)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                        )}
                        {payslip.status === 'finalized' && (
                          <button
                            onClick={async () => {
                              try {
                                const { data: { session } } = await supabase.auth.getSession();
                                if (!session?.access_token) {
                                  toast.error('Not authenticated');
                                  return;
                                }

                                const response = await fetch(`${API_BASE_URL}/api/payslips/${payslip.id}/pdf`, {
                                  headers: {
                                    'Authorization': `Bearer ${session.access_token}`
                                  }
                                });

                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `payslip-${payslip.payslipId || payslip.teamMember.name}-${payslip.payPeriodStart}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                  toast.success('Payslip downloaded successfully');
                                } else {
                                  throw new Error('Failed to download payslip');
                                }
                              } catch (error: any) {
                                console.error('Error downloading payslip:', error);
                                toast.error(error.message || 'Failed to download payslip');
                              }
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Download PDF
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedPayslip(payslip);
                            setShowDeleteConfirm(true);
                          }}
                          className="text-red-500 hover:text-red-700"
                          title="Delete payslip"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Payslip Confirm Modal */}
      {showDeleteConfirm && selectedPayslip && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Payslip</h3>
            <p className="text-sm text-gray-600 mb-1">
              Are you sure you want to delete the payslip for <strong>{selectedPayslip.teamMember.displayName || selectedPayslip.teamMember.name}</strong>?
            </p>
            <p className="text-xs text-gray-400 mb-6">The record will be kept in the system but removed from the list.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setSelectedPayslip(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePayslip}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate / Edit Payslip Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full mx-4 max-h-[92vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">{editingPayslipId ? 'Edit Payslip' : 'Generate Payslip'}</h3>
                <button
                  onClick={closeGenerateModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
              <div className="space-y-4 min-w-0">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Team Member *</label>
                    <select
                      value={formData.userGymMappingId}
                      onChange={(e) => setFormData(prev => ({ ...prev, userGymMappingId: e.target.value }))}
                      disabled={!!editingPayslipId}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      required
                    >
                      <option value="">Select Team Member</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.displayName || member.name}
                        </option>
                      ))}
                    </select>
                    {editingPayslipId && (
                      <p className="text-xs text-gray-500 mt-1">Team member can't be changed once a draft exists.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pay Date *</label>
                    <input
                      type="date"
                      value={formData.payDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, payDate: e.target.value }))}
                      max={getEndOfNextMonth()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum: End of next month
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pay Period Start *</label>
                    <input
                      type="date"
                      value={formData.payPeriodStart}
                      onChange={(e) => setFormData(prev => ({ ...prev, payPeriodStart: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pay Period End *</label>
                    <input
                      type="date"
                      value={formData.payPeriodEnd}
                      onChange={(e) => setFormData(prev => ({ ...prev, payPeriodEnd: e.target.value }))}
                      max={getEndOfNextMonth()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum: End of next month
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={fetchGenerationData}
                    disabled={loadingGenerationData || !formData.userGymMappingId || !formData.payPeriodStart || !formData.payPeriodEnd}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {loadingGenerationData && (
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {loadingGenerationData ? 'Loading...' : 'Load Details'}
                  </button>
                </div>

                {loadingGenerationData && !generationData && (
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                    <LoadingSpinner size="sm" />
                    <span className="text-sm text-blue-800">Loading payslip details…</span>
                  </div>
                )}

                {/* Attendance Stats - Informational Only */}
                {generationData && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900">Attendance Statistics</h4>
                      <span className="text-xs text-gray-500 bg-blue-100 text-blue-800 px-2 py-1 rounded">Informational Only</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">Pay defaults to monthly salary. Add attendance-related deductions manually as components below.</p>
                    {generationData.attendance.futureDays && generationData.attendance.futureDays > 0 && (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        {generationData.attendance.futureDays} day(s) in the pay period are in the future, so attendance is not considered for those days.
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Total Days</div>
                        <div className="text-lg font-semibold text-gray-900">{generationData.attendance.totalDays}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Present Days</div>
                        <div className="text-lg font-semibold text-green-600">{generationData.attendance.presentDays}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Leave Days</div>
                        <div className="text-lg font-semibold text-blue-600">{generationData.attendance.leaveDays}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Absent Days</div>
                        <div className="text-lg font-semibold text-red-600">{generationData.attendance.absentDays}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Overtime Hours</div>
                        <NumberInput
                          step="0.5"
                          min={0}
                          emptyValue={0}
                          value={formData.overtimeHours}
                          onChange={(v) => setFormData(prev => ({ ...prev, overtimeHours: v ?? 0 }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Undertime Hours</div>
                        <NumberInput
                          step="0.5"
                          min={0}
                          emptyValue={0}
                          value={formData.undertimeHours}
                          onChange={(v) => setFormData(prev => ({ ...prev, undertimeHours: v ?? 0 }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Approved Leaves Breakdown */}
                    {generationData.leaves && generationData.leaves.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="text-sm font-medium text-gray-700 mb-2">Approved Leaves in Period</div>
                        <div className="space-y-1.5">
                          {generationData.leaves.map(leave => {
                            const start = new Date(leave.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                            const end = new Date(leave.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                            const typeLabel = leave.leaveType.charAt(0).toUpperCase() + leave.leaveType.slice(1).replace(/_/g, ' ');
                            return (
                              <div key={leave.id} className="flex items-center justify-between text-xs bg-blue-50 rounded px-2 py-1.5">
                                <span className="font-medium text-blue-800">{typeLabel}</span>
                                <span className="text-blue-600">
                                  {leave.isHalfDay
                                    ? `${start} (Half Day${leave.halfDayType ? ' – ' + leave.halfDayType.replace('_', ' ') : ''})`
                                    : start === end ? start : `${start} – ${end}`
                                  }
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {generationData.attendance.leaveDays > 0 && (!generationData.leaves || generationData.leaves.length === 0) && (
                      <div className="mt-2 text-xs text-gray-500 italic">Leave records were counted but details are unavailable.</div>
                    )}
                  </div>
                )}

                {/* Trainer Engagement (only shown for team members with role=trainer) */}
                {generationData?.trainerEngagement && (
                  <div className="bg-violet-50/50 rounded-lg p-4 border border-violet-200 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">Trainer Member Engagement</h4>
                        <p className="text-xs text-gray-600 mt-0.5">
                          For incentive planning. Members at or above the eligibility threshold are highlighted.
                        </p>
                      </div>
                      <span className="text-xs text-violet-700 bg-violet-100 px-2 py-1 rounded font-bold">
                        {engagementView?.eligibleMemberCount ?? 0} eligible / {generationData.trainerEngagement.memberCount} total
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs font-semibold text-gray-700">Eligible ≥</label>
                      <NumberInput
                        integer
                        min={0}
                        max={100}
                        emptyValue={0}
                        value={engagementThreshold}
                        onChange={v => setEngagementThreshold(v ?? 0)}
                        className="w-20 px-2 py-1 rounded border border-violet-200 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-violet-300"
                      />
                      <span className="text-xs text-gray-600">% engagement</span>
                      {engagementThreshold !== gymEngagementThreshold ? (
                        <button
                          onClick={() => setEngagementThreshold(gymEngagementThreshold)}
                          className="text-xs text-violet-700 underline hover:text-violet-900"
                        >
                          reset to gym default ({gymEngagementThreshold}%)
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">gym default</span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white rounded-md p-2.5">
                        <div className="text-xs text-gray-500">Eligible Members</div>
                        <div className="text-xl font-bold text-emerald-600">{engagementView?.eligibleMemberCount ?? 0}</div>
                      </div>
                      <div className="bg-white rounded-md p-2.5">
                        <div className="text-xs text-gray-500">Total Check-ins</div>
                        <div className="text-xl font-bold text-gray-900">{generationData.trainerEngagement.totalCheckIns}</div>
                      </div>
                      <div className="bg-white rounded-md p-2.5">
                        <div className="text-xs text-gray-500">Engaged Days</div>
                        <div className="text-xl font-bold text-gray-900">{generationData.trainerEngagement.totalEngagementDays}</div>
                      </div>
                    </div>

                    {(engagementView?.members.length ?? 0) === 0 ? (
                      <div className="text-xs text-gray-500 text-center py-2">No assigned members in this period.</div>
                    ) : (
                      <div className="overflow-x-auto overflow-y-auto max-h-56 bg-white rounded-md">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500 sticky top-0 z-10">
                            <tr>
                              <th className="text-left px-3 py-2">Member</th>
                              <th className="text-right px-3 py-2">Engagement</th>
                              <th className="text-right px-3 py-2">Check-ins</th>
                              <th className="text-center px-3 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {engagementView?.members.map(m => (
                              <tr key={m.memberId}>
                                <td className="px-3 py-2 text-gray-800">
                                  {m.memberName}
                                  {!m.currentlyAssigned && <span className="ml-2 text-[10px] font-bold text-gray-400 uppercase">past</span>}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-xs">
                                  <span className={m.eligible ? 'font-bold text-emerald-700' : 'text-gray-400'}>
                                    {m.engagementPercent.toFixed(1)}% ({m.engagementDays}d)
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-xs text-gray-600">{m.checkIns}</td>
                                <td className="px-3 py-2 text-center">
                                  {m.eligible ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">Eligible</span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500">Below {engagementThreshold}%</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Salary Summary */}
                {generationData && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-gray-900 mb-2">Salary Summary</h4>
                    {generationData.salary && generationData.salary.baseSalary !== undefined ? (
                      <>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Base Monthly Salary:</span>
                            <span className="ml-2 font-semibold text-gray-900">
                              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: generationData.salary.currency || 'INR' }).format(generationData.salary.baseSalary)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Gross Salary (Default):</span>
                            <span className="ml-2 font-semibold text-green-600">
                              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: generationData.salary.currency || 'INR' }).format(generationData.salary.baseSalary)}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          {generationData.salary.baseSalary === 0
                            ? 'Base salary is ₹0. Add salary components manually below or update team member salary settings.'
                            : 'Add deductions or additions below to adjust the final amount.'}
                        </p>
                        {generationData.salary.baseSalary > 0 && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.prorate}
                                onChange={(e) => setFormData(prev => ({ ...prev, prorate: e.target.checked }))}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-800">
                                <span className="font-medium">Prorate salary</span> for this pay period
                                <span className="block text-xs text-gray-600">
                                  Pays only for the days in the selected period, based on the number of days in that calendar month. Use this for a partial month (e.g. someone joining or leaving mid-month).
                                </span>
                              </span>
                            </label>
                            {formData.prorate && payslipPreview?.prorateInfo && (
                              <p className="text-xs text-blue-800 bg-blue-100 rounded px-2 py-1 mt-2 inline-block">
                                Prorated for {payslipPreview.prorateInfo.periodDays} of {payslipPreview.prorateInfo.monthDays} days → {formatCurrency(payslipPreview.grossSalary)}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-gray-600">Loading salary information...</p>
                    )}
                  </div>
                )}

                {/* Additional Components */}
                {generationData && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-900">Salary Components</h4>
                      <button
                        onClick={addComponent}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add Component
                      </button>
                    </div>
                    <p className="text-xs text-gray-600">Add attendance-related deductions (e.g., "Absent Days Deduction", "Late Coming Deduction") or other components as needed.</p>

                    {componentsError && (
                      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        {componentsError}
                      </div>
                    )}

                    {formData.components.map((component, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={component.componentType}
                              onChange={(e) => updateComponent(index, 'componentType', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="benefit">Benefit</option>
                              <option value="reimbursement">Reimbursement</option>
                              <option value="deduction">Deduction</option>
                              <option value="attendance_deduction">Attendance Deduction</option>
                              <option value="leave_deduction">Leave Deduction</option>
                              <option value="bonus">Bonus</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                            <input
                              type="text"
                              value={component.componentName}
                              onChange={(e) => updateComponent(index, 'componentName', e.target.value)}
                              placeholder="e.g., HRA, Medical"
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                componentsError && !component.componentName.trim() ? 'border-red-400' : 'border-gray-300'
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                            <NumberInput
                              emptyValue={0}
                              value={component.amount}
                              onChange={(v) => updateComponent(index, 'amount', v ?? 0)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex items-end space-x-2">
                            <label className="flex items-center space-x-1">
                              <input
                                type="checkbox"
                                checked={component.isDeduction}
                                onChange={(e) => updateComponent(index, 'isDeduction', e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs text-gray-700">Deduction</span>
                            </label>
                            <button
                              onClick={() => removeComponent(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Description (Optional)</label>
                          <input
                            type="text"
                            value={component.description || ''}
                            onChange={(e) => updateComponent(index, 'description', e.target.value)}
                            placeholder="Optional description"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Notes */}
                {generationData && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Additional notes..."
                    />
                  </div>
                )}
              </div>

              {/* Live payslip preview */}
              <div className="lg:sticky lg:top-0 self-start">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                  <div className="p-4 pb-3 border-b border-gray-100">
                    <h4 className="font-semibold text-gray-900">Payslip Preview</h4>
                    {generationData && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {generationData.teamMember.displayName || generationData.teamMember.name}
                        {formData.payDate && ` · ${new Date(formData.payDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`}
                      </p>
                    )}
                  </div>

                  {!generationData ? (
                    <p className="text-xs text-gray-500 p-4">Load details on the left to see a live preview here.</p>
                  ) : !payslipPreview ? (
                    <p className="text-xs text-gray-500 p-4">Loading preview...</p>
                  ) : (
                    <>
                      <div className="p-4 space-y-2.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                          Payslip automatically considers
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center"><CheckIcon /></span>
                            <span className="text-sm text-gray-700">Attendance</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{generationData.attendance.presentDays} present</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center"><CheckIcon /></span>
                            <span className="text-sm text-gray-700">Leaves</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{generationData.attendance.leaveDays} day{generationData.attendance.leaveDays === 1 ? '' : 's'}</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center"><CheckIcon /></span>
                            <span className="text-sm text-gray-700">Base salary</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(generationData.salary.baseSalary)}</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center"><CheckIcon /></span>
                            <span className="text-sm text-gray-700">Proration & deductions</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{formData.prorate ? 'Prorated' : 'Full month'}</span>
                        </div>

                        {generationData.trainerEngagement && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-violet-500 text-white flex items-center justify-center"><CheckIcon /></span>
                              <span className="text-sm text-gray-700">Trainer performance</span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">{engagementView?.eligibleMemberCount ?? 0} eligible</span>
                          </div>
                        )}
                      </div>

                      {generationData.trainerEngagement && engagementView && engagementView.members.length > 0 && (
                        <div className="mx-4 mb-4 bg-violet-50 border border-violet-100 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-violet-800">Trainer performance</span>
                            <span className="text-[11px] font-bold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">
                              {engagementView.eligibleMemberCount} eligible / {engagementView.members.length}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {engagementView.members.slice(0, 3).map(m => (
                              <div key={m.memberId} className="flex items-center justify-between text-xs">
                                <span className="text-gray-700 truncate pr-2">{m.memberName}</span>
                                <span className={m.eligible ? 'font-semibold text-emerald-700 whitespace-nowrap' : 'text-gray-400 whitespace-nowrap'}>
                                  {m.engagementPercent.toFixed(0)}% {m.eligible ? 'ELIGIBLE' : `BELOW ${engagementThreshold}%`}
                                </span>
                              </div>
                            ))}
                            {engagementView.members.length > 3 && (
                              <p className="text-[11px] text-violet-600 pt-1">+{engagementView.members.length - 3} more</p>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="mx-4 mb-4 bg-gray-50 border border-gray-200 rounded-xl divide-y divide-gray-200">
                        {payslipPreview.lines.length === 0 ? (
                          <div className="px-3 py-3 text-xs text-gray-500">No earnings or deductions yet.</div>
                        ) : (
                          payslipPreview.lines.map((line, i) => (
                            <div key={i} className="flex items-start justify-between px-3 py-2 text-sm">
                              <div>
                                <div className="text-gray-800">{line.name}</div>
                                {line.description && <div className="text-xs text-gray-500">{line.description}</div>}
                              </div>
                              <div className={line.isDeduction ? 'text-red-600' : 'text-gray-900'}>
                                {line.isDeduction ? '−' : ''}{formatCurrency(line.amount)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="bg-zinc-950 px-4 py-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Net Payable</p>
                        <p className="text-xs text-gray-500 mb-1">
                          after {formData.prorate ? 'proration, ' : ''}deductions{(formData.overtimeHours > 0 || payslipPreview.lines.some(l => !l.isDeduction && l.name !== 'Basic Salary')) ? ' & bonus' : ''}
                        </p>
                        <p className="text-2xl font-bold text-[#AAD816]">{formatCurrency(payslipPreview.netSalary)}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={closeGenerateModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPayslip}
                  disabled={!generationData || !formData.userGymMappingId || !formData.payPeriodStart || !formData.payPeriodEnd || !formData.payDate}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {editingPayslipId ? 'Update Payslip' : 'Generate Payslip'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-right">
                Payslip can be generated up to one month in advance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* View Payslip Modal */}
      {showPayslipModal && selectedPayslip && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Payslip Details</h3>
                <button
                  onClick={() => {
                    setShowPayslipModal(false);
                    setSelectedPayslip(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Payslip Content - Minimal Professional Design (Razorpay Style) */}
              <div id="payslip-content" className="bg-white p-8" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                {/* Header */}
                <div className="mb-6 pb-4 border-b-2 border-gray-900">
                  <div className="flex justify-between items-start">
                    <div>
                      {selectedPayslip.gym && (
                        <>
                          <h1 className="text-2xl font-bold text-gray-900 mb-1">
                            {selectedPayslip.gym.displayName || selectedPayslip.gym.name}
                          </h1>
                          {(selectedPayslip.gym.registeredAddress || selectedPayslip.gym.address) && (
                            <p className="text-sm text-gray-600 mb-1">
                              {selectedPayslip.gym.registeredAddress || selectedPayslip.gym.address}
                            </p>
                          )}
                          <div className="text-sm text-gray-600">
                            {selectedPayslip.gym.phoneNumbers && selectedPayslip.gym.phoneNumbers.length > 0 && (
                              <span className="mr-4">Ph: {selectedPayslip.gym.phoneNumbers[0]}</span>
                            )}
                            {selectedPayslip.gym.email && (
                              <span>Email: {selectedPayslip.gym.email}</span>
                            )}
                          </div>
                          {selectedPayslip.gym.gstin && selectedPayslip.gym.includeGstinInInvoices !== false && (
                            <p className="text-sm text-gray-900 font-medium mt-1">GSTIN: {selectedPayslip.gym.gstin}</p>
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-right">
                      <h2 className="text-xl font-bold text-gray-900 mb-1">PAYSLIP</h2>
                      <p className="text-xs text-gray-600">ID: {selectedPayslip.payslipId}</p>
                    </div>
                  </div>
                </div>

                {/* Employee and Period Info - Simple Table */}
                <div className="mb-6">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-2 pr-4 font-semibold text-gray-900" style={{ width: '25%' }}>Employee Name</td>
                        <td className="py-2 text-gray-700">{selectedPayslip.teamMember.name || selectedPayslip.teamMember.displayName}</td>
                        <td className="py-2 pr-4 font-semibold text-gray-900" style={{ width: '25%' }}>Pay Period</td>
                        <td className="py-2 text-gray-700">
                          {formatDate(selectedPayslip.payPeriodStart)} to {formatDate(selectedPayslip.payPeriodEnd)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4 font-semibold text-gray-900">Employee ID</td>
                        <td className="py-2 text-gray-700">
                          {selectedPayslip.teamMember.employeeId || selectedPayslip.teamMember.id.substring(0, 8).toUpperCase()}
                        </td>
                        <td className="py-2 pr-4 font-semibold text-gray-900">Pay Date</td>
                        <td className="py-2 text-gray-700">{formatDate(selectedPayslip.payDate)}</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4 font-semibold text-gray-900">Designation</td>
                        <td className="py-2 text-gray-700">{selectedPayslip.teamMember.role.toUpperCase()}</td>
                        <td className="py-2 pr-4 font-semibold text-gray-900">Branch</td>
                        <td className="py-2 text-gray-700">{selectedPayslip.gym?.displayName || ''}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Attendance Summary */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-300">ATTENDANCE SUMMARY</h3>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Total Days</p>
                      <p className="font-bold text-gray-900">{selectedPayslip.totalDays}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Present</p>
                      <p className="font-bold text-gray-900">{selectedPayslip.presentDays}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Leave</p>
                      <p className="font-bold text-gray-900">{Number(selectedPayslip.leaveDays).toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Absent</p>
                      <p className="font-bold text-gray-900">{selectedPayslip.absentDays}</p>
                    </div>
                  </div>
                  {(Number(selectedPayslip.overtimeHours) > 0 || Number(selectedPayslip.undertimeHours) > 0) && (
                    <div className="grid grid-cols-4 gap-4 text-sm mt-3">
                      {Number(selectedPayslip.overtimeHours) > 0 && (
                        <div>
                          <p className="text-gray-600 mb-1">Overtime Hours</p>
                          <p className="font-bold text-gray-900">{Number(selectedPayslip.overtimeHours).toFixed(1)}h</p>
                        </div>
                      )}
                      {Number(selectedPayslip.undertimeHours) > 0 && (
                        <div>
                          <p className="text-gray-600 mb-1">Undertime Hours</p>
                          <p className="font-bold text-gray-900">{Number(selectedPayslip.undertimeHours).toFixed(1)}h</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Salary Breakdown - Clean Table */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-300">SALARY DETAILS</h3>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-2 font-semibold text-gray-900">EARNINGS</th>
                        <th className="text-right py-2 font-semibold text-gray-900">AMOUNT (₹)</th>
                        <th className="text-left py-2 pl-8 font-semibold text-gray-900">DEDUCTIONS</th>
                        <th className="text-right py-2 font-semibold text-gray-900">AMOUNT (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const earnings = selectedPayslip.components.filter(c => !c.isDeduction);
                        const deductions = selectedPayslip.components.filter(c => c.isDeduction);
                        const maxRows = Math.max(
                          earnings.length || 1,
                          deductions.length
                        );

                        const rows = [];
                        for (let i = 0; i < maxRows; i++) {
                          const earning = earnings[i];
                          const deduction = deductions[i];

                          rows.push(
                            <tr key={i} className="border-b border-gray-100">
                              <td className="py-2 text-gray-700">
                                {earning ? earning.componentName : (i === 0 && earnings.length === 0 ? 'Basic Salary' : '')}
                                {earning?.description && (
                                  <div className="text-xs text-gray-500">{earning.description}</div>
                                )}
                              </td>
                              <td className="py-2 text-right text-gray-900">
                                {earning ? `₹${earning.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
                                  (i === 0 && earnings.length === 0 ? `₹${selectedPayslip.grossSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '')}
                              </td>
                              <td className="py-2 pl-8 text-gray-700">
                                {deduction?.componentName || ''}
                                {deduction?.description && (
                                  <div className="text-xs text-gray-500">{deduction.description}</div>
                                )}
                              </td>
                              <td className="py-2 text-right text-gray-900">
                                {deduction ? `₹${deduction.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                              </td>
                            </tr>
                          );
                        }
                        return rows;
                      })()}
                      <tr className="border-t-2 border-gray-900 font-bold">
                        <td className="py-3 text-gray-900">TOTAL EARNINGS</td>
                        <td className="py-3 text-right text-gray-900">
                          ₹{selectedPayslip.grossSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 pl-8 text-gray-900">TOTAL DEDUCTIONS</td>
                        <td className="py-3 text-right text-gray-900">
                          ₹{selectedPayslip.totalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Net Salary */}
                <div className="mb-6 p-4 bg-gray-100 border border-gray-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">NET SALARY</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ₹{selectedPayslip.netSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="text-right text-sm text-gray-700">
                      <p className="font-medium">{convertToWords(selectedPayslip.netSalary)} Only</p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedPayslip.notes && (
                  <div className="mb-6 p-3 border border-gray-300">
                    <p className="text-xs font-semibold text-gray-900 mb-1">NOTES:</p>
                    <p className="text-xs text-gray-700">{selectedPayslip.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 pt-4 text-center">
                  <p className="text-xs text-gray-600">This is a computer-generated payslip. No signature required.</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {selectedPayslip.status === 'finalized' && (
                  <button
                    onClick={async () => {
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session?.access_token) {
                          toast.error('Not authenticated');
                          return;
                        }

                        const response = await fetch(`${API_BASE_URL}/api/payslips/${selectedPayslip.id}/pdf`, {
                          headers: {
                            'Authorization': `Bearer ${session.access_token}`
                          }
                        });

                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `payslip-${selectedPayslip.payslipId || selectedPayslip.teamMember.name}-${selectedPayslip.payPeriodStart}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          toast.success('Payslip downloaded successfully');
                        } else {
                          throw new Error('Failed to download payslip');
                        }
                      } catch (error: any) {
                        console.error('Error downloading payslip:', error);
                        toast.error(error.message || 'Failed to download payslip');
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download PDF</span>
                  </button>
                )}
                {selectedPayslip.status === 'draft' && (
                  <button
                    onClick={async () => {
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session?.access_token) {
                          toast.error('Not authenticated');
                          return;
                        }

                        const response = await fetch(`${API_BASE_URL}/api/payslips/${selectedPayslip.id}`, {
                          method: 'PATCH',
                          headers: {
                            'Authorization': `Bearer ${session.access_token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            status: 'finalized'
                          })
                        });

                        if (response.ok) {
                          toast.success('Payslip finalized successfully');
                          setShowPayslipModal(false);
                          setSelectedPayslip(null);
                          fetchPayslips();
                        } else {
                          const errorData = await response.json();
                          throw new Error(errorData.error || 'Failed to finalize payslip');
                        }
                      } catch (error: any) {
                        console.error('Error finalizing payslip:', error);
                        toast.error(error.message || 'Failed to finalize payslip');
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Finalize Payslip
                  </button>
                )}
                {selectedPayslip.status === 'draft' && (
                  <button
                    onClick={() => handleEditPayslip(selectedPayslip)}
                    className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowPayslipModal(false);
                    setSelectedPayslip(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
